import { STAFF_ROLE_LABELS, STAFF_ROLE_ORDER, type StaffRole } from '@/data/staff-roles'
import type { TShowRoleAssignment } from '@schemas'

export type StaffRosterMember = {
  id: string
  role: StaffRole
  name: string
  contact: string | null
  personId: string | null | undefined
  isMember: boolean | null | undefined
}

export type StaffRosterGroup = {
  role: StaffRole
  label: string
  members: StaffRosterMember[]
}

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

const SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v'])

function buildMemberId(assignment: TShowRoleAssignment, fallbackIndex: number): string {
  if (typeof assignment.id === 'string' && assignment.id.trim().length > 0) {
    return assignment.id
  }

  const personId = assignment.personId
  if (typeof personId === 'string' && personId.trim().length > 0) {
    return `${assignment.role}-${personId}`
  }

  return `${assignment.role}-${fallbackIndex}`
}

export function createStaffRosterMember(
  assignment: TShowRoleAssignment,
  fallbackIndex = 0
): StaffRosterMember {
  return {
    id: buildMemberId(assignment, fallbackIndex),
    role: assignment.role,
    name: resolveStaffName(assignment),
    contact: resolveStaffContact(assignment),
    personId: assignment.personId,
    isMember: assignment.person?.ahstcMember,
  }
}

export function resolveStaffName(assignment: TShowRoleAssignment): string {
  const fromPerson = assignment.person?.name
  if (typeof fromPerson === 'string' && fromPerson.trim().length > 0) {
    return fromPerson.trim()
  }
  const fallback = assignment.name
  if (typeof fallback === 'string' && fallback.trim().length > 0) {
    return fallback.trim()
  }
  return 'Unnamed staff member'
}

export function resolveStaffContact(assignment: TShowRoleAssignment): string | null {
  const fromAssignment = assignment.contact
  if (typeof fromAssignment === 'string' && fromAssignment.trim().length > 0) {
    return fromAssignment.trim()
  }
  const email = assignment.person?.email
  if (typeof email === 'string' && email.trim().length > 0) {
    return email.trim()
  }
  const phone = assignment.person?.phone
  if (typeof phone === 'string' && phone.trim().length > 0) {
    return phone.trim()
  }
  return null
}

type SortParts = { last: string; remainder: string; fallback: string }

function computeSortParts(name: string, fallback: string): SortParts {
  const normalized = name.trim().replace(/\s+/g, ' ')
  if (!normalized) {
    return { last: '\uffff', remainder: '\uffff', fallback }
  }

  if (normalized.includes(',')) {
    const [last, ...rest] = normalized.split(',')
    const remainder = rest.join(',').trim()
    return {
      last: last.trim().toLocaleLowerCase(),
      remainder: remainder.toLocaleLowerCase(),
      fallback,
    }
  }

  const parts = normalized.split(' ')
  let last = parts[parts.length - 1]
  let remainderParts = parts.slice(0, -1)
  if (parts.length > 1 && SUFFIXES.has(last.toLocaleLowerCase())) {
    last = parts[parts.length - 2]
    remainderParts = [...parts.slice(0, -2), parts[parts.length - 1]]
  }
  const remainder = remainderParts.join(' ').trim()

  return {
    last: last.toLocaleLowerCase(),
    remainder: remainder.toLocaleLowerCase(),
    fallback,
  }
}

export function compareStaffRosterMembers(a: StaffRosterMember, b: StaffRosterMember): number {
  const partsA = computeSortParts(a.name, a.id)
  const partsB = computeSortParts(b.name, b.id)

  const lastDelta = collator.compare(partsA.last, partsB.last)
  if (lastDelta !== 0) return lastDelta

  const remainderDelta = collator.compare(partsA.remainder, partsB.remainder)
  if (remainderDelta !== 0) return remainderDelta

  const fullDelta = collator.compare(a.name.toLocaleLowerCase(), b.name.toLocaleLowerCase())
  if (fullDelta !== 0) return fullDelta

  return collator.compare(partsA.fallback, partsB.fallback)
}

export function sortStaffRosterMembers(members: StaffRosterMember[]): StaffRosterMember[] {
  return [...members].sort(compareStaffRosterMembers)
}

export function groupStaffAssignments(assignments: TShowRoleAssignment[]): StaffRosterGroup[] {
  const groups: StaffRosterGroup[] = []

  for (const role of STAFF_ROLE_ORDER) {
    const label = STAFF_ROLE_LABELS[role].plural
    const members = assignments
      .filter(assignment => assignment.role === role)
      .map<StaffRosterMember>((assignment, index) => createStaffRosterMember(assignment, index))

    if (members.length === 0) continue
    groups.push({ role, label, members: sortStaffRosterMembers(members) })
  }

  return groups
}

export function countStaffByRole(assignments: TShowRoleAssignment[]): Record<StaffRole, number> {
  return STAFF_ROLE_ORDER.reduce<Record<StaffRole, number>>(
    (acc, role) => {
      acc[role] = assignments.filter(assignment => assignment.role === role).length
      return acc
    },
    {} as Record<StaffRole, number>
  )
}
